import type { CalendarAreaRef } from '@blocksuite/affine/data-view';
export type {
  CalendarAreaRef,
  CalendarMailboxLink,
  CalendarMailboxLinkDecision,
  CalendarMailboxLinkMailbox,
  CalendarMailboxLinkOperationPolicies,
  CalendarMailboxLinkPatch,
  CalendarMailboxLinkPolicy,
  CalendarMailboxLinkReason,
  CalendarMailboxLinksResponse,
  CalendarMailboxLinkVisibility,
} from '@blocksuite/affine/data-view';
import type { WorkspaceAreaRef } from '../../folder-api';
import type {
  ViewBackedAppSourceAttachInput,
  ViewBackedAppSourcePreferencePatch,
  ViewBackedAppSourceRef,
} from '../view-backed-app/types';

export type CalendarMode = 'day' | 'month' | 'week';

export type CalendarSourceLifecycleState = 'active' | 'deleted' | 'trashed';

export type CalendarSourceScope = 'private' | 'shared';

export type CalendarSourceSyncState = 'failed' | 'local' | 'pending' | 'synced';

export interface CalendarEventColumnMap {
  allDayCol?: string;
  attendeesCol?: string;
  calendarIdCol?: string;
  descriptionCol?: string;
  endDateCol?: string;
  icsSequenceCol?: string;
  icsUidCol?: string;
  invitationMethodCol?: string;
  invitationStateCol?: string;
  locationPhysicalCol?: string;
  locationVirtualCol?: string;
  recurrenceCol?: string;
  recurrenceExdateCol?: string;
  recurrenceIdCol?: string;
  reminderCol?: string;
  sourceMailAccountIdCol?: string;
  sourceMailAttachmentBlobKeyCol?: string;
  sourceMailMessageIdCol?: string;
  sourceTypeCol?: string;
  startDateCol?: string;
  statusCol?: string;
  timezoneCol?: string;
  titleColumn?: string;
  visibilityCol?: string;
}

export interface CalendarSource {
  accessDocId: string;
  appKind: ViewBackedAppSourceRef['appKind'];
  area: CalendarAreaRef | null;
  blockId: string;
  canEdit: boolean;
  canManage: boolean;
  color: string | null;
  columns?: CalendarEventColumnMap;
  databaseId: string;
  deletedAt: string | null;
  docId: string;
  id: string;
  lifecycleState: CalendarSourceLifecycleState;
  owner: CalendarSourceOwner | null;
  presentationError: 'owner_profile_unavailable' | null;
  projectionError?: string | null;
  scope: CalendarSourceScope;
  sourcePresentationContractVersion: 1;
  syncState?: CalendarSourceSyncState;
  title: string;
  trashedAt: string | null;
  viewId: string;
  viewMode: string;
  visible: boolean;
}

export interface CalendarSourceOwner {
  avatarUrl: string | null;
  displayName: string;
  userId: string;
}

export interface CalendarSourcesResponse {
  sources: CalendarSource[];
}

export interface CalendarInvitationAssignment {
  calendarRowId: string | null;
  sourceId: string;
}

export interface CalendarInvitationAssignmentsResponse {
  assignments: CalendarInvitationAssignment[];
}

export type CalendarSourceAttachInput = ViewBackedAppSourceAttachInput;

export type CalendarSourcePreferencePatch = ViewBackedAppSourcePreferencePatch;

export type CalendarMailboxAreaTransitionResource = 'calendar' | 'mailbox';
export type CalendarMailboxAreaTransitionAction =
  | 'move_together'
  | 'unlink_and_move';

export interface CalendarMailboxAreaTransitionCalendarRef {
  calendarDatabaseId: string;
  calendarDocId: string;
  calendarViewId: string;
}

export interface CalendarMailboxAreaTransitionPreview {
  activeLinkCount: number;
  calendarCount: number;
  calendarRefs?: CalendarMailboxAreaTransitionCalendarRef[];
  calendarTitles: string[];
  canMoveTogether: boolean;
  canUnlinkAndMove: boolean;
  componentDetailsVisible: boolean;
  mailboxCount: number;
  mailboxTitles: string[];
  moveTogetherUnavailableReason: 'insufficient_permissions' | null;
  requiresTransition: boolean;
  resource: CalendarMailboxAreaTransitionResource;
  targetArea: CalendarAreaRef;
  unlinkAndMoveUnavailableReason: 'insufficient_permissions' | null;
}

export interface CalendarMailboxAreaTransitionResult {
  movedCalendarCount: number;
  movedMailboxCount: number;
  removedLinkCount: number;
  targetArea: CalendarAreaRef;
}

export type CalendarMailboxLinkMode = 'both' | 'inbound' | 'outbound';

export type CalendarMailboxLinkUpdateMode = CalendarMailboxLinkMode | 'status';

export const CALENDAR_MAILBOX_LINK_MODES = [
  'both',
  'inbound',
  'outbound',
] as const satisfies readonly CalendarMailboxLinkMode[];

export interface CalendarSourceCandidate {
  appKind: ViewBackedAppSourceRef['appKind'];
  area: CalendarAreaRef | null;
  blockId: string;
  databaseId: string;
  docId: string;
  documentTitle: string | null;
  projectionError?: string | null;
  title: string;
  viewId: string;
  viewMode: string;
}

export interface CalendarSourceCandidatesResponse {
  candidates: CalendarSourceCandidate[];
}

export interface CalendarProjectedEvent {
  allDay: boolean;
  attendeeCount: number;
  canEdit: boolean;
  color: string | null;
  endAt: string;
  icsSequence: number | null;
  icsUid: string | null;
  id: string;
  invitationMethod: string | null;
  invitationState: string | null;
  lifecycleState: CalendarSourceLifecycleState;
  physicalLocation: string | null;
  rowId: string;
  sourceId: string;
  sourceMailAccountId: string | null;
  sourceMailAttachmentBlobKey: string | null;
  sourceMailMessageId: string | null;
  sourceType: string | null;
  startAt: string;
  status: string | null;
  timezone: string;
  title: string;
  virtualLocation: string | null;
}

export type CalendarInvitationResponseStatus =
  | 'accepted'
  | 'declined'
  | 'tentative';

export interface CalendarInvitationResponseResult {
  delivery: {
    deliveryKey: string;
    issueReason?: string;
    status: 'failed' | 'sent' | 'skipped';
  };
  invitationState: CalendarInvitationResponseStatus | 'cancelled' | 'pending';
}

export interface CalendarProjectedEventWire extends CalendarProjectedEvent {
  sourceTitle: string;
}

export interface CalendarEventsResponse {
  calendarEventContractVersion: 2;
  events: CalendarProjectedEvent[];
  sources: Array<{
    canEdit: boolean;
    canManage: boolean;
    color: string | null;
    error: string | null;
    id: string;
    lifecycleState: CalendarSourceLifecycleState;
    title: string;
    visible: boolean;
  }>;
  window: {
    endAt: string;
    mode: CalendarMode;
    startAt: string;
  };
}

export type CalendarSourceRef = ViewBackedAppSourceRef;

export function toCalendarAreaRef(
  area: WorkspaceAreaRef | CalendarAreaRef
): CalendarAreaRef {
  if ('systemFolderId' in area) {
    return area;
  }
  return {
    kind: area.kind,
    name: area.name,
    ownerUserId: area.ownerUserId,
    rootFolderId: area.rootFolderId,
    systemFolderId: null,
    workspaceId: area.workspaceId,
  };
}

export function calendarAreaScope(
  area: Pick<CalendarAreaRef, 'kind'> | null
): CalendarSourceScope {
  return area?.kind === 'private' ? 'private' : 'shared';
}
